<template>
	<div>
		<div class="brick view_brick">
			<div class="view_inner">
				<h3 class="area-title">Images</h3>

				<section class="media-section">
					<h4 class="section-title">Logo Image</h4>
					<p class="section-hint">Recommended ratio: 1:1 to 3:1 (e.g., 85×64px or 84×29px).</p>
					<MediaUploader v-model="logoForm" category="LOGO" :max-count="1" @error="onError" />
					<div class="section-actions">
						<button type="button" @click="save('LOGO')" :disabled="saving">Save Logo Image</button>
					</div>
				</section>

				<hr class="divider" />

				<section class="media-section">
					<h4 class="section-title">Banner Image</h4>
					<p class="section-hint">Recommended size: 1920×471px (~4:1 ratio).</p>
					<MediaUploader v-model="bannerForm" category="BANNER" :max-count="10" @error="onError" />
					<div class="section-actions">
						<button type="button" @click="save('BANNER')" :disabled="saving">Save Banner Image</button>
					</div>
				</section>

				<hr class="divider" />

				<section class="media-section">
					<h4 class="section-title">Sub Banner Image</h4>
					<p class="section-hint">Recommended size: 1920×106px (~18:1 ratio).</p>
					<MediaUploader v-model="subBannerForm" category="SUB_BANNER" :max-count="1" @error="onError" />
					<div class="section-actions">
						<button type="button" @click="save('SUB_BANNER')" :disabled="saving">Save Sub Banner Image</button>
					</div>
				</section>
			</div>
		</div>

		<div class="brick view_brick settings-card">
			<div class="view_inner">
				<h3 class="area-title">Settings</h3>
				<table class="view_tbl">
					<colgroup>
						<col style="width: 180px" />
						<col style="width: 240px" />
						<col />
					</colgroup>
					<tbody>
						<tr v-for="f in settingFields" :key="f.key">
							<th class="top_th">{{ f.label }}</th>
							<td>
								<input v-if="f.type === 'text'" type="text" class="tbl_int" style="width: 220px" v-model="optionForm[f.key]" :placeholder="f.placeholder" maxlength="255" />
								<input v-else-if="f.type === 'checkbox'" type="checkbox" v-model="optionForm[f.key]" />
								<select v-else-if="f.type === 'select'" class="tbl_int" style="width: 220px" v-model="optionForm[f.key]">
									<option v-for="o in f.options" :key="o.value" :value="o.value">{{ o.label }}</option>
								</select>
							</td>
							<td class="field-desc">{{ f.description }}</td>
						</tr>
					</tbody>
				</table>
				<div class="view_bottom_bt">
					<button type="button" class="save-btn" @click="saveOptions" :disabled="saving">Save</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import MediaUploader from '@/components/common/MediaUploader.vue';
import { apiGetMedia, apiSaveMedia, apiGetOptions, apiSaveOptions } from '@/api/basicinfo/retail_setting';

export default {
	name: 'RetailSetting',
	components: { MediaUploader },
	data() {
		return {
			logoForm: { items: [] },
			bannerForm: { items: [] },
			subBannerForm: { items: [] },
			optionForm: { item_option_label: 'COLORS', item_image_visible: true, item_display_unit: 'class' },
			settingFields: [
				{
					key: 'item_option_label',
					label: 'Option Label',
					type: 'text',
					placeholder: 'Leave empty to hide',
					description: 'Label shown above the item variant list on the itemClass page (e.g. COLORS). Leave empty to hide the label.',
				},
				{
					key: 'item_image_visible',
					label: 'Show Item Image',
					type: 'checkbox',
					description: "Show each item variant's thumbnail image on the itemClass page.",
				},
				{
					key: 'item_display_unit',
					label: 'List Display Unit',
					type: 'select',
					options: [
						{ value: 'class', label: 'Class (default)' },
						{ value: 'item', label: 'Item' },
					],
					description: 'Display unit for retail store list pages (search / main). Class: current behavior. Item: individual items with quantity + add to cart. Applied after the customer re-logins.',
				},
			],
			saving: false,
		};
	},
	async created() {
		this.$store.commit('setSideIndex', 9);
		this.$store.commit('setSelectedMenu', 'nav_li_retail_setting');
		this.$store.commit('setSubTitle', 'Basic Information -> Retail Setting');

		await Promise.all([this.load('LOGO'), this.load('BANNER'), this.load('SUB_BANNER'), this.loadOptions()]);
	},
	methods: {
		formKey(category) {
			return { LOGO: 'logoForm', BANNER: 'bannerForm', SUB_BANNER: 'subBannerForm' }[category];
		},
		async load(category) {
			try {
				const res = await apiGetMedia(category);
				const items = (res.data && res.data.items) || [];
				this[this.formKey(category)] = { items };
			} catch (err) {
				this.onError(this.errMsg(err));
			}
		},
		async save(category) {
			this.saving = true;
			const form = this[this.formKey(category)];
			try {
				await apiSaveMedia(category, form.items);
				this.$toast.success('Saved successfully');
				await this.load(category);
			} catch (err) {
				this.onError(this.errMsg(err));
			} finally {
				this.saving = false;
			}
		},
		async loadOptions() {
			try {
				const res = await apiGetOptions();
				const d = (res && res.data) || {};
				this.optionForm = {
					item_option_label: d.item_option_label == null ? 'COLORS' : d.item_option_label,
					item_image_visible: d.item_image_visible !== false && d.item_image_visible !== 0,
					item_display_unit: d.item_display_unit === 'item' ? 'item' : 'class',
				};
			} catch (err) {
				this.onError(this.errMsg(err));
			}
		},
		async saveOptions() {
			this.saving = true;
			try {
				await apiSaveOptions({ ...this.optionForm });
				this.$toast.success('Saved successfully');
				await this.loadOptions();
			} catch (err) {
				this.onError(this.errMsg(err));
			} finally {
				this.saving = false;
			}
		},
		onError(msg) {
			this.$toast.error(msg);
		},
		errMsg(err) {
			return (err.response && err.response.data && err.response.data.message) || err.message || 'Request failed';
		},
	},
};
</script>

<style scoped>
.area-title {
	margin: 0 0 16px 0;
	font-size: 18px;
	font-weight: 700;
	color: #222;
	padding-bottom: 8px;
	border-bottom: 2px solid #1e88e5;
}
.settings-card {
	margin-top: 16px;
}
.media-section {
	padding: 12px 0;
}
.section-title {
	margin: 0 0 12px 0;
	font-size: 15px;
	font-weight: 700;
	color: #333;
}
.section-hint {
	margin: -4px 0 12px 0;
	font-size: 12px;
	color: #888;
}
.section-actions {
	margin-top: 16px;
}
.section-actions button,
.save-btn {
	padding: 8px 20px;
	background: #1e88e5;
	color: #fff;
	border: none;
	border-radius: 3px;
	cursor: pointer;
	font-size: 14px;
}
.section-actions button[disabled],
.save-btn[disabled] {
	background: #aac;
	cursor: not-allowed;
}
.divider {
	border: 0;
	border-top: 1px solid #eee;
	margin: 20px 0;
}
.field-desc {
	font-size: 12px;
	color: #888;
	line-height: 1.5;
}
</style>
